// =====================================================
// QUERY BUILDER UTILITY
// Usa la función RPC dynamic_component_query
// =====================================================

import { supabase } from '../lib/supabase'
import type { ComponentConfig } from '../types/builder.types'
import {
  getBmpLayerIdFromBaseTable,
  getBmpLayerUrl,
  isBmpBaseTable,
} from '../lib/bmp-layers'
import { repairSqlSelectListConcatArtifact } from './concat-field-sanitize'

/**
 * execute_custom_sql (y dynamic_component_query) envuelven el SQL como:
 * SELECT json_agg(...) FROM (<query>) t
 * Un punto y coma al final del SELECT provoca: syntax error at or near ";"
 */
export function sanitizeSqlForExecuteCustomSql(query: string): string {
  let s = repairSqlSelectListConcatArtifact(query.trim())
  while (s.endsWith(';')) {
    s = s.slice(0, -1).trim()
  }
  return s
}

/**
 * Ejecuta una query según la configuración del componente usando la RPC
 */
export async function executeComponentQuery(config: ComponentConfig): Promise<any> {
  const { 
    dataSource, 
    componentType, 
    metadata, 
    styling,
    customQuery,
    chartConfig,
    tableConfig,
    kpiConfig,
    filterMunicipio,
    filterEspecie,
    filters,
    aggregation,
    dataField,
  } = config

  try {

    

     let isCKANResource = dataSource.startsWith('ckan_data_generic[') 
     
     let isMapCKAN = config.componentType === 'map';

     if(isMapCKAN){
      return await executeCKANQueryMap(config)
     }

      if (config.dataSource === "custom_query" && 
          config.customQuery?.enabled && 
          config.customQuery.config?.baseTable &&
          config.customQuery.config?.baseTable.startsWith('ckan_data_generic[')) {
            isCKANResource = true;
      }
    
    if (isCKANResource) {
      return await executeCKANQuery(config)
    }

    if (
      config.dataSource === 'custom_query' &&
      config.customQuery?.enabled &&
      config.customQuery.config?.baseTable &&
      isBmpBaseTable(config.customQuery.config.baseTable)
    ) {
      return await executeBmpLayerQuery(config)
    }

    // Preparar configuración para la RPC
    const rpcConfig: any = {
      dataSource,
      componentType,
      metadata: metadata || {},
      styling: styling || {},
      filters: filters || {}
    }

    let availableFilters: string[] = [];
    
    // Primero intentar desde customQuery.config.filters
    if (customQuery?.config?.filters && Array.isArray(customQuery.config?.filters)) {
      availableFilters = customQuery.config?.filters;
      console.log("Filtros encontrados en customQuery.config.filters:", availableFilters);
    }
    // Luego desde config.filters (si es un array)
    else if (filters && Array.isArray(filters)) {
      availableFilters = filters;
      console.log("Filtros encontrados en config.filters:", availableFilters);
    }
    // Finalmente desde config.filter (propiedad obsoleta)
    else if (config.filter && Array.isArray(config.filter)) {
      availableFilters = config.filter;
      console.log("Filtros encontrados en config.filter:", availableFilters);
    }

    // Aplicar filtros si están disponibles
    if (availableFilters.includes('municipio') && filterMunicipio) {
      rpcConfig.filterMunicipio = filterMunicipio;
      console.log("Aplicando filtro municipio:", filterMunicipio);
    }

    if (availableFilters.includes('especie') && filterEspecie) {
      rpcConfig.filterEspecie = filterEspecie;
      console.log("Aplicando filtro especie:", filterEspecie);
    }

    // AGREGAR LOS FILTROS DIRECTAMENTE SI VIENEN EN EL COMPONENTE
    // Esto es para cuando los filtros vienen como props del ComponentRenderer
    if (filterMunicipio && !rpcConfig.filterMunicipio) {
      rpcConfig.filterMunicipio = filterMunicipio;
      console.log("Aplicando filtro municipio (directo):", filterMunicipio);
    }

    if (filterEspecie && !rpcConfig.filterEspecie) {
      rpcConfig.filterEspecie = filterEspecie;
      console.log("Aplicando filtro especie (directo):", filterEspecie);
    }

    // Agregar configuraciones específicas según el tipo
    if (customQuery && customQuery.enabled) {
      rpcConfig.customQuery =
        typeof customQuery.sql === 'string'
          ? { ...customQuery, sql: sanitizeSqlForExecuteCustomSql(customQuery.sql) }
          : { ...customQuery }
    }

    if (chartConfig) {
      rpcConfig.chartConfig = chartConfig
    }

    if (tableConfig) {
      rpcConfig.tableConfig = tableConfig
    }

    if (kpiConfig) {
      rpcConfig.kpiConfig = kpiConfig
    }

    // Para KPIs sin customQuery, agregar dataField y aggregation
    if (componentType === 'kpi' && !customQuery?.enabled && dataField && aggregation) {
      rpcConfig.dataField = dataField
      rpcConfig.aggregation = aggregation 
    }

    
    

    const supabaseAny = supabase as any;

    // @ts-ignore - execute_custom_sql es una función RPC creada en Supabase
    const { data, error } = await supabaseAny
      .rpc('dynamic_component_query', {
        p_config: rpcConfig
      })

    if (error) {
      console.error('Error en RPC:', error)
      throw error
    }

    const dataArray = data as any;

    // Procesar respuesta según el tipo
    if (componentType === 'kpi') {
      return dataArray?.value || 0
    }

    return dataArray.data || []
  } catch (error) {
    console.error('Error executing component query:', error)
    
    // Fallback: usar método antiguo si la RPC falla
    return await executeLegacyQuery(config)
  }
}

type FilterOp = '=' | '>' | '<' | '>=' | '<=' | 'ILIKE'

function matchFilter(row: Record<string, any>, field: string, operator: FilterOp, value: string): boolean {
  const raw = row[field]
  if (value === '' && operator === '=') return raw == null || raw === ''
  const cell = raw == null ? '' : String(raw)
  const val = value
  switch (operator) {
    case '=':
      return cell === val
    case 'ILIKE':
      return cell.toLowerCase().includes(val.toLowerCase())
    case '>':
      return Number(cell) > Number(val)
    case '<':
      return Number(cell) < Number(val)
    case '>=':
      return Number(cell) >= Number(val)
    case '<=':
      return Number(cell) <= Number(val)
    default:
      return true
  }
}

/** Extrae el nombre del campo de un identificador "tabla.campo" o "campo" */
function fieldKey(fullField: string): string {
  return fullField.includes('.') ? fullField.split('.').pop()! : fullField
}

/** Normaliza clave para join: mismo valor numérico o string sin espacios */
function normalizeJoinKey(v: any): string {
  const s = String(v ?? '').trim()
  const n = Number(s)
  if (s !== '' && !Number.isNaN(n)) return String(n)
  return s
}

type AggregationSpec = { field: string; agreggation: string; innerSql?: string }

/** Evalúa una expresión de agregado tipo SQL sobre una fila BMP (identificadores tabla.campo y COALESCE). */
function bmpEvaluateAggregateInnerSql(row: Record<string, any>, innerSql: string): number {
  let s = innerSql.trim()
  const re = /\b([a-z_][a-z0-9_]*\.[a-z_][a-z0-9_]*)\b/g
  s = s.replace(re, (m) => {
    const v = row[m]
    const n = Number(v)
    return Number.isFinite(n) ? String(n) : 'null'
  })
  try {
    const COALESCE = (...args: unknown[]) => {
      for (const a of args) {
        if (a == null) continue
        const n = Number(a)
        if (!Number.isNaN(n)) return n
      }
      return Number(args[args.length - 1])
    }
    const fn = new Function('COALESCE', `"use strict"; return (${s});`) as (
      c: typeof COALESCE
    ) => number
    return Number(fn(COALESCE))
  } catch {
    return NaN
  }
}

function aggregateBmpValues(agg: string, values: number[]): number {
  if (!values.length) return 0
  switch (agg.toUpperCase()) {
    case 'AVG': return values.reduce((a, b) => a + b, 0) / values.length
    case 'SUM': return values.reduce((a, b) => a + b, 0)
    case 'COUNT': return values.length
    case 'MIN': return Math.min(...values)
    case 'MAX': return Math.max(...values)
    default: return values[0]
  }
}

/**
 * Ejecuta una "consulta" sobre una capa BMP (GeoJSON): carga el archivo,
 * resuelve joins (ej. municipios por CODMUN), aplica GROUP BY + agregaciones si hay, filters, limit y offset.
 */
async function executeBmpLayerQuery(config: ComponentConfig): Promise<any> {
  const baseTable = config.customQuery?.config?.baseTable
  if (!baseTable || !isBmpBaseTable(baseTable)) return []

  const layerId = getBmpLayerIdFromBaseTable(baseTable)
  const url = layerId ? getBmpLayerUrl(layerId) : null
  if (!url) return []

  const res = await fetch(url)
  const geojson = await res.json().catch(() => null)
  const features = geojson?.features
  if (!Array.isArray(features)) return []

  let rows: Record<string, any>[] = features.map((f: any) => ({ ...(f.properties || {}) }))

  const cfg = config.customQuery?.config
  const joins = (cfg?.joins || []) as Array<{ table: string; on: { localField: string; foreignField: string } }>

  for (const join of joins) {
    const tableName = join.table
    const localKey = fieldKey(join.on.localField)
    const foreignKey = fieldKey(join.on.foreignField)

    const { data: joinRows, error } = await supabase.from(tableName).select('*')
    if (error || !joinRows?.length) continue

    const lookup = new Map<string, Record<string, any>>()
    joinRows.forEach((r: Record<string, any>) => {
      const k = normalizeJoinKey(r[foreignKey])
      if (!lookup.has(k)) lookup.set(k, r)
    })

    const prefix = tableName + '.'
    const altLocalKey = localKey === 'CODMUN' ? 'c_mun' : localKey === 'c_mun' ? 'CODMUN' : null
    rows = rows.map((row) => {
      let key = normalizeJoinKey(row[localKey])
      if ((key === '' || key === undefined) && altLocalKey && row[altLocalKey] != null) key = normalizeJoinKey(row[altLocalKey])
      const joined = lookup.get(key) || {}
      const withJoin: Record<string, any> = { ...row }
      Object.keys(joined).forEach((k) => {
        withJoin[prefix + k] = joined[k]
      })
      return withJoin
    })
  }

  const filterMunicipio = config.filterMunicipio
  if (filterMunicipio) {
    const hasMunicipiosJoin = joins.some((j) => j.table === 'municipios')
    if (hasMunicipiosJoin) {
      rows = rows.filter((row) => {
        const id = row['municipios.id_municipio']
        return id != null && String(id) === String(filterMunicipio)
      })
    } else {
      const { data } = await supabase
        .from('municipios')
        .select('codigo_ine')
        .eq('id_municipio', filterMunicipio)
        .maybeSingle()
      const muni = data as { codigo_ine?: string } | null
      const codigoIne = muni?.codigo_ine != null ? normalizeJoinKey(muni.codigo_ine) : null
      if (codigoIne != null) {
        rows = rows.filter((row) => {
          const code = normalizeJoinKey(row['CODMUN'] ?? row['c_mun'])
          return code === codigoIne
        })
      }
    }
  }

  const filters = (cfg?.filters || []) as Array<{ field: string; operator: FilterOp; value: string }>
  const limit = cfg?.limit ?? 5000
  const offset = cfg?.offset ?? 0
  const groupByFields = (cfg?.groupByFields || []) as string[]
  const aggregation = (cfg?.agreggation || []) as AggregationSpec[]
  const selectedFields = (cfg?.selectedFields || []) as string[]

  const activeFilters = filters.filter((f) => f.value !== '' || f.operator === '=')
  rows = rows.filter((row) => activeFilters.every((f) => matchFilter(row, f.field, f.operator, f.value)))

  if (groupByFields.length > 0 && aggregation.length > 0) {
    const aggFieldsSet = new Set(aggregation.map((a) => a.field))
    const groupKey = (row: Record<string, any>) =>
      groupByFields.map((f) => normalizeJoinKey(row[f])).join('\0')
    const groups = new Map<string, Record<string, any>[]>()
    rows.forEach((row) => {
      const k = groupKey(row)
      if (!groups.has(k)) groups.set(k, [])
      groups.get(k)!.push(row)
    })
    rows = Array.from(groups.entries()).map(([, groupRows]) => {
      const first = groupRows[0]
      const out: Record<string, any> = {}
      selectedFields.forEach((f) => {
        if (aggFieldsSet.has(f)) {
          const spec = aggregation.find((a) => a.field === f)
          if (spec) {
            const vals = groupRows
              .map((r) => {
                if (spec.innerSql?.trim()) {
                  const v = bmpEvaluateAggregateInnerSql(r, spec.innerSql.trim())
                  return Number.isFinite(v) ? v : NaN
                }
                return Number(r[f])
              })
              .filter((n) => !Number.isNaN(n))
            out[f] = aggregateBmpValues(spec.agreggation, vals)
          }
        } else {
          out[f] = first[f]
        }
      })
      return out
    })
    rows = rows.slice(offset, offset + limit)
  } else {
    if (selectedFields.length > 0) {
      rows = rows.map((r) => {
        const out: Record<string, any> = {}
        selectedFields.forEach((k) => {
          if (k in r) out[k] = r[k]
        })
        return out
      })
    }
    rows = rows.slice(offset, offset + limit)
  }

  if (config.componentType === 'kpi') {
    const kpiConfig = config.kpiConfig
    const dataField = (kpiConfig as any)?.dataField || (config as any).dataField
    const aggregation = (kpiConfig as any)?.aggregation || (config as any).aggregation
    if (dataField && rows.length > 0) {
      const vals = rows.map((r) => Number(r[dataField])).filter((n) => !Number.isNaN(n))
      if (aggregation === 'SUM') return vals.reduce((a, b) => a + b, 0)
      if (aggregation === 'AVG') return vals.length ? vals.reduce((a, b) => a + b, 0) / vals.length : 0
      if (aggregation === 'COUNT') return vals.length
      if (aggregation === 'MIN') return Math.min(...vals)
      if (aggregation === 'MAX') return Math.max(...vals)
    }
    return rows.length
  }

  return rows
}

/**
 * Método legacy para compatibilidad
 */
async function executeLegacyQuery(config: ComponentConfig): Promise<any> { 
  const { dataSource, customQuery, filterMunicipio, filterEspecie, filter } = config

   let isCKANResource = dataSource.startsWith('ckan_data_generic[')

   if (config.dataSource === "custom_query" && 
        config.customQuery?.enabled && 
        config.customQuery.config?.baseTable &&
        config.customQuery.config?.baseTable.startsWith('ckan_data_generic[')) {
          isCKANResource = true;
    }
  if (isCKANResource) {
    return await executeCKANQuery(config)
  }

  // Si tiene customQuery, ejecutarla directamente
  if (customQuery && customQuery.enabled && customQuery.sql) {
    // Aplicar filtros solo si están en el array filter
    let filteredSQL = customQuery.sql;
    
    if (filter && filter.includes('municipio') && filterMunicipio) {
      filteredSQL = applyMunicipioFilter(filteredSQL, filterMunicipio);
    }
    
    if (filter && filter.includes('especie') && filterEspecie) {
      filteredSQL = applyEspecieFilter(filteredSQL, filterEspecie);
    }
    
    // Para compatibilidad: si no hay array filter pero hay valores
    if (!filter && filterMunicipio) {
      filteredSQL = applyMunicipioFilter(filteredSQL, filterMunicipio);
    }
    
    if (!filter && filterEspecie) {
      filteredSQL = applyEspecieFilter(filteredSQL, filterEspecie);
    }

    // @ts-ignore - execute_custom_sql es una función RPC creada en Supabase
    const { data, error } = await supabase.rpc('execute_custom_sql', {
      query: sanitizeSqlForExecuteCustomSql(filteredSQL),
    })

    if (error) throw error

  const dataArray = data as any;
    
    // Para KPIs, extraer el valor
    if (config.componentType === 'kpi' && Array.isArray(dataArray) && dataArray.length > 0) {
      return dataArray[0].value !== undefined ? dataArray[0].value : dataArray[0][Object.keys(dataArray[0])[0]]
    }
    
    return dataArray || []
  }

  // Para consultas simples sin customQuery
  let query = supabase.from(dataSource).select('*')

  // Aplicar filtros solo si están en el array filter
  if (filter && filter.includes('municipio') && filterMunicipio && dataSource !== 'municipios') {
    const { data: municipioData } = await supabase
      .from('municipios')
      .select('id_municipio')
      .eq('nombre', filterMunicipio)
      .single()

    if (municipioData) {
      query = query.eq('id_municipio', (municipioData as any).id_municipio)
    }
  }

  if (filter && filter.includes('especie') && filterEspecie) {
    query = query.eq('codigo_especie', filterEspecie)
  }
  
  // Para compatibilidad: si no hay array filter pero hay valores
  if (!filter) {
    if (filterMunicipio && dataSource !== 'municipios') {
      const { data: municipioData } = await supabase
        .from('municipios')
        .select('id_municipio')
        .eq('nombre', filterMunicipio)
        .single()

      if (municipioData) {
        query = query.eq('id_municipio', (municipioData as any).id_municipio)
      }
    }

    if (filterEspecie) {
      query = query.eq('codigo_especie', filterEspecie)
    }
  }

  const { data, error } = await query
  if (error) throw error

  return data || []
}

/**
 * Aplica filtro de municipio a SQL
 */
function applyMunicipioFilter(sql: string, filterMunicipio: string): string {
  let modifiedSQL = sql
  
  // Buscar JOIN con municipios
  const hasMunicipiosJoin = /JOIN\s+municipios\s+(AS\s+\w+)?/i.test(sql)
  
  if (hasMunicipiosJoin) {
    // Extraer el alias de municipios
    const joinMatch = sql.match(/JOIN\s+municipios\s+(AS\s+(\w+))?/i)
    const municipioAlias = joinMatch && joinMatch[2] ? joinMatch[2] : 'municipios'
    
    // Agregar filtro por nombre de municipio
    const whereCondition = `${municipioAlias}.nombre = '${filterMunicipio}'`
    modifiedSQL = addWhereCondition(modifiedSQL, whereCondition)
  } else {
    // Si no hay JOIN, buscar campo id_municipio
    const hasMunicipioField = /id_municipio/i.test(sql)
    if (hasMunicipioField) {
      // Necesitamos obtener el id_municipio del nombre
      // Esto se manejaría mejor con una subconsulta
      const whereCondition = `id_municipio = (SELECT id_municipio FROM municipios WHERE nombre = '${filterMunicipio}')`
      modifiedSQL = addWhereCondition(modifiedSQL, whereCondition)
    }
  }

  return modifiedSQL
}

/**
 * Aplica filtro de especie a SQL
 */
function applyEspecieFilter(sql: string, filterEspecie: number): string {
  let modifiedSQL = sql
  
  // Buscar JOIN con especies
  const hasEspeciesJoin = /JOIN\s+especies\s+(AS\s+\w+)?/i.test(sql)
  
  if (hasEspeciesJoin) {
    // Extraer el alias de especies
    const joinMatch = sql.match(/JOIN\s+especies\s+(AS\s+(\w+))?/i)
    const especieAlias = joinMatch && joinMatch[2] ? joinMatch[2] : 'especies'
    
    // Agregar filtro por código de especie
    const whereCondition = `${especieAlias}.codigo_especie = ${filterEspecie}`
    modifiedSQL = addWhereCondition(modifiedSQL, whereCondition)
  } else {
    // Si no hay JOIN, buscar campo codigo_especie
    const hasEspecieField = /codigo_especie/i.test(sql)
    if (hasEspecieField) {
      const whereCondition = `codigo_especie = ${filterEspecie}`
      modifiedSQL = addWhereCondition(modifiedSQL, whereCondition)
    }
  }

  return modifiedSQL
}

/**
 * Agrega condición WHERE a una consulta SQL
 */
function addWhereCondition(sql: string, condition: string): string {
  const hasWhere = /\bWHERE\b/i.test(sql)
  
  if (hasWhere) {
    return sql.replace(/(WHERE\s+)(.*?)(\s+(GROUP|ORDER|LIMIT|$))/i, `$1$2 AND ${condition}$3`)
  } else {
    return sql.replace(/(\s+(GROUP|ORDER|LIMIT|$))/i, ` WHERE ${condition}$1`)
  }
}

/**
 * Función específica para manejar queries CKAN
 */
/*async function executeCKANQueryV1(config: ComponentConfig): Promise<any> {
  const { 
    
    customQuery, 
    filterMunicipio, 
    filterEspecie, 
    filters, 
    componentType, 
    dataField, 
    aggregation 
  } = config
  let dataSource = config.dataSource

  // Detectar si es CKAN
  let isCKANResource = dataSource.startsWith('ckan_data_generic[')

  if (config.dataSource === "custom_query" && 
        config.customQuery?.enabled && 
        config.customQuery.config?.baseTable &&
        config.customQuery.config.baseTable.startsWith('ckan_data_generic[')) {
          isCKANResource = true;
    }
  if (!isCKANResource) {
    throw new Error('No es un recurso CKAN')
  }

  if (config.dataSource === "custom_query" &&  
        config.customQuery?.enabled && 
        config.customQuery.config?.baseTable &&
        config.customQuery.config.baseTable.startsWith('ckan_data_generic[')) {
          dataSource = config.customQuery.config.baseTable
    }
    console.log("dataSource", dataSource); 

  // Extraer nombre del recurso
  const match = dataSource.match(/ckan_data_generic\[(.*?)\]/)
  if (!match) {
    throw new Error('Invalid CKAN resource syntax. Use: ckan_data_generic[resource_name]')
  }
  const resourceName = match[1]

  // CASO 1: Custom Query con SQL directo
  if (customQuery && customQuery.enabled && customQuery.sql) {
    let sql = customQuery.sql
    
    // Aplicar filtros si existen
    const availableFilters = config.filter || []
    
    if (availableFilters.includes('municipio') && filterMunicipio) {
      sql = applyMunicipioFilter(sql, filterMunicipio)
    }
    
    if (availableFilters.includes('especie') && filterEspecie) {
      sql = applyEspecieFilter(sql, filterEspecie)
    }

    // @ts-ignore - execute_custom_sql es una función RPC
    const { data, error } = await supabase.rpc('execute_custom_sql', {
      query: sanitizeSqlForExecuteCustomSql(sql),
    })

    if (error) throw error

    // Para KPIs con custom query, esperamos un solo valor
    if (componentType === 'kpi') {
      if (Array.isArray(data) && data.length > 0) {
        return data[0].value !== undefined ? data[0].value : data[0][Object.keys(data[0])[0]]
      }
      return data
    }

    return data || []
  }

  // CASO 2: Query estándar para CKAN
  let query = supabase.from('ckan_data_generic').select('data')
  
  // Filtrar por resource_name
  query = query.eq('resource_name', resourceName)
  
  // Aplicar filtros al campo data (JSONB)
  if (filters) {
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        query = query.filter('data', 'cs', JSON.stringify({ [key]: value }))
      }
    })
  }

  const { data, error } = await query
  if (error) throw error

  // Extraer los datos del campo data
  let processedData = (data || []).map(row => row.data)

  // Para KPIs: aplicar agregación
  if (componentType === 'kpi' && aggregation && dataField) {
    return applyAggregation(processedData, dataField, aggregation)
  }

  return processedData
}*/


/**
 * Función específica para manejar querys de mapas
 */
async function executeCKANQueryMap(config: ComponentConfig): Promise<any> {


  const dataSource = config.dataSource;
  const innerMatch = dataSource.match(/ckan_data_generic\[(.*?)\]/);
  let resourceName:any = '';
  if (innerMatch) {
    resourceName = innerMatch[1];
  }

  let query = supabase.from('ckan_sync_status').select('storage_path_processed'); 
  query = query.eq('resource_name', resourceName);
  query = query.eq('status', 'ready');
  const { data, error } = await query;
  if (error) throw error;

  let processedData = (data || []).map((row:any) => row.storage_path_processed);
  return processedData;

}

/**
 * Función específica para manejar queries CKAN
 */
async function executeCKANQuery(config: ComponentConfig): Promise<any> {
  const { 
    customQuery, 
    filterMunicipio, 
    filterEspecie, 
    filters, 
    componentType, 
    dataField, 
    aggregation 
  } = config;

  let dataSource = config.dataSource

  

  // Detectar si es CKAN
  let isCKANResource = dataSource.startsWith('ckan_data_generic[');

  if (config.dataSource === "custom_query" && 
          config.customQuery?.enabled && 
          config.customQuery.config?.baseTable &&
          config.customQuery.config?.baseTable.startsWith('ckan_data_generic[')) {
            isCKANResource = true;
      }
  if (!isCKANResource) {
    throw new Error('No es un recurso CKAN');
  }

  if (config.dataSource === "custom_query" &&  
        config.customQuery?.enabled && 
        config.customQuery.config?.baseTable &&
        config.customQuery.config?.baseTable.startsWith('ckan_data_generic[')) {
          
          dataSource = config.customQuery.config?.baseTable 
    }

    

    dataSource = "ckan_data_generic[datos_de_numero_de_defunciones_en_castilla_y_leon]";


    

  // Extraer nombre del recurso del dataSource
  const match = dataSource.match(/ckan_data_generic\[(.*?)\]/); 
  
  if (!match) {
    throw new Error('Invalid CKAN resource syntax. Use: ckan_data_generic[resource_name]');
  }
  const resourceName = match[1];

  // CASO 1: Custom Query con SQL directo
  if (customQuery && customQuery.enabled && customQuery.sql) {
    let sql = customQuery.sql;
    
    // Aplicar filtros si existen
    const availableFilters = config.filter || [];
    
    if (availableFilters.includes('municipio') && filterMunicipio) {
      sql = applyMunicipioFilter(sql, filterMunicipio);
    }
    
    if (availableFilters.includes('especie') && filterEspecie) {
      sql = applyEspecieFilter(sql, filterEspecie);
    }

    if(isCKANResource){
        sql = buildCKANQueryFromConfig(customQuery.config, resourceName);
    }

    console.log("sql", sql);

    // @ts-ignore - execute_custom_sql es una función RPC
    const { data, error } = await supabase.rpc('execute_custom_sql', {
      query: sanitizeSqlForExecuteCustomSql(sql),
    });

    if (error) throw error;

    const dataArray = data as any[];

    // Para KPIs con custom query, esperamos un solo valor
    if (componentType === 'kpi') {
      if (Array.isArray(dataArray) && dataArray.length > 0) {
        return dataArray[0].value !== undefined ? dataArray[0].value : data[0][Object.keys(dataArray[0])[0]];
      }
      return dataArray;
    }

    return dataArray || [];
  }

  // CASO 2: Custom Query con configuración (sin SQL directo)
  if (customQuery && customQuery.enabled && customQuery.config) {
    // Extraer el nombre real del recurso del baseTable
    let actualResourceName = resourceName;
    
    // Si el resourceName contiene otro ckan_data_generic[], extraer el interno
    if (resourceName.includes('ckan_data_generic[')) {
      const innerMatch = resourceName.match(/ckan_data_generic\[(.*?)\]/);
      if (innerMatch) {
        actualResourceName = innerMatch[1];
      }
    }

    
    // Construir SQL a partir de la configuración
    const sql = buildCKANQueryFromConfig(customQuery.config, actualResourceName);
    
    // @ts-ignore - execute_custom_sql es una función RPC
    const { data, error } = await supabase.rpc('execute_custom_sql', {
      query: sanitizeSqlForExecuteCustomSql(sql),
    });

    if (error) {
      console.error('Error ejecutando consulta CKAN:', error);
      throw error;
    }

    const dataArray = data as any[];

    // Para KPIs con custom query, esperamos un solo valor
    if (componentType === 'kpi') {
      if (Array.isArray(data) && dataArray.length > 0) {
        return dataArray[0].value !== undefined ? dataArray[0].value : dataArray[0][Object.keys(dataArray[0])[0]];
      }
      return dataArray;
    }

    return dataArray || [];
  }

  // CASO 3: Query estándar para CKAN (fallback)
  let query = supabase.from('ckan_data_generic').select('data');
  
  // Filtrar por resource_name
  query = query.eq('resource_name', resourceName);
  
  // Aplicar filtros al campo data (JSONB)
  if (filters) {
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        query = query.filter('data', 'cs', JSON.stringify({ [key]: value }));
      }
    });
  }

  const { data, error } = await query;
  if (error) throw error;

  // Extraer los datos del campo data
  let processedData = (data || []).map((row:any) => row.data);

  // Para KPIs: aplicar agregación
  if (componentType === 'kpi' && aggregation && dataField) {
    return applyAggregation(processedData, dataField, aggregation);
  }

  return processedData;
}

function buildCKANQueryFromConfig(config: any, resourceName: string): string {
  // Extraer campos seleccionados
  const selectedFields = config.selectedFields || [];
  const groupByFields = (config.groupByFields || []);
  const fieldAliases = (config.fieldAliases || {}) as Record<string, string>;
  const computedExpressions = (config.computedColumnExpressions || {}) as Record<string, string>;
  const escapeSqlAlias = (a: string) => String(a).replace(/"/g, '""');

  // Crear mapa de agregaciones (QueryBuilder guarda `agreggation`, no `aggregation`)
  const aggregationMap = new Map<string, { fn: string; innerSql?: string }>();
  const aggSource = config.aggregation || config.agreggation;
  if (aggSource && Array.isArray(aggSource)) {
    aggSource.forEach((agg: any) => {
      const fn = agg.aggregation || agg.agreggation;
      if (agg.field && fn) {
        aggregationMap.set(agg.field, { fn, innerSql: agg.innerSql });
      }
    });
  }

  // Construir la cláusula SELECT
  let selectClause = '';
  if (selectedFields.length > 0) {
    const selectParts = selectedFields.map((field: string) => {
      if (field.startsWith('__computed_expr_')) {
        const inner = computedExpressions[field]?.trim();
        const alias = escapeSqlAlias(fieldAliases[field] || field);
        if (!inner) {
          return `NULL::text AS "${alias}"`;
        }
        return `(${inner}) AS "${alias}"`;
      }

      // Verificar si el campo está en GROUP BY
      const isInGroupBy = groupByFields.includes(field);
      
      // Verificar si tiene una agregación específica
      const hasAggregation = aggregationMap.has(field);
      const aggEntry = aggregationMap.get(field);
      const aggregationType = aggEntry?.fn;
      
      if (isInGroupBy) {
        // Campo en GROUP BY: extraer como texto (sin agregación)
        return `data->>'${field}' AS "${field}"`;
      } else if (hasAggregation && aggEntry) {
        if (aggEntry.innerSql?.trim()) {
          return `${aggregationType}(${aggEntry.innerSql.trim()}) AS "${field}"`;
        }
        // Campo con agregación específica
        // Determinar si es un campo numérico para hacer cast apropiado
        const lowerField = field.toLowerCase();
        const isLikelyNumeric = lowerField.includes('población') || 
                               lowerField.includes('total') || 
                               lowerField.includes('número') ||
                               lowerField.includes('nº') ||
                               lowerField.includes('cantidad') ||
                               lowerField.includes('valor') ||
                               lowerField.includes('superficie') ||
                               lowerField.includes('área') ||
                               lowerField.includes('ha') ||
                               lowerField.includes('hectáreas') ||
                               /\d/.test(field) || // Si contiene dígitos
                               field.includes('('); // Si tiene paréntesis (unidades)
        
        if (isLikelyNumeric) {
          // Para campos numéricos, usar cast a numeric
          return `${aggregationType}((data->>'${field}')::numeric) AS "${field}"`;
        } else {
          // Para campos no numéricos, intentar convertir a texto o usar la función de agregación directamente
          // Algunas funciones como COUNT o MAX/MIN pueden trabajar con texto
          if (aggregationType === 'COUNT') {
            return `COUNT((data->>'${field}')::numeric) AS "${field}"`;
          } else {
            return `${aggregationType}((data->>'${field}')::numeric) AS "${field}"`;
          }
        }
      } else {
        // Campo sin GROUP BY ni agregación específica
        // Mantener la lógica existente para compatibilidad
        const lowerField = field.toLowerCase();
        const isLikelyNumeric = lowerField.includes('población') || 
                               lowerField.includes('total') || 
                               lowerField.includes('número') ||
                               lowerField.includes('nº') ||
                               lowerField.includes('cantidad') ||
                               lowerField.includes('valor') ||
                               lowerField.includes('superficie') ||
                               lowerField.includes('área') ||
                               lowerField.includes('ha') ||
                               lowerField.includes('hectáreas') ||
                               /\d/.test(field) ||
                               field.includes('(');
        
        if (isLikelyNumeric) {
          return `SUM((data->>'${field}')::numeric) AS "${field}"`;
        } else {
          return `data->>'${field}' AS "${field}"`;
        }
      }
    });
    
    selectClause = selectParts.join(', ');
  } else {
    selectClause = 'data';
  }

  // ... resto del código (WHERE, GROUP BY, LIMIT, etc.) se mantiene igual
  // Construir la cláusula WHERE (filtros)
  let whereClause = `resource_name = '${resourceName}'`;
  
  if (config.filters && config.filters.length > 0) {
    config.filters.forEach((filter: any) => {
      const fieldName = filter.field;
      const operator = filter.operator;
      let value = filter.value;
      
      value = value.replace(/'/g, "''");
      
      const lowerField = fieldName.toLowerCase();
      const isNumericField = lowerField.includes('fecha') || 
                            lowerField.includes('año') ||
                            lowerField.includes('población') ||
                            lowerField.includes('total') ||
                            /\d/.test(fieldName);
      
      if (operator === 'ILIKE') {
        whereClause += ` AND data->>'${fieldName}' ILIKE '%${value}%'`;
      } else if (isNumericField) {
        whereClause += ` AND (data->>'${fieldName}')::numeric ${operator} ${value}`;
      } else {
        whereClause += ` AND data->>'${fieldName}' ${operator} '${value}'`;
      }
    });
  }

  // Construir la cláusula GROUP BY
  let groupByClause = '';
  if (groupByFields.length > 0) {
    const groupByParts = groupByFields.map((field: string) => {
      if (field.startsWith('__computed_expr_')) {
        const inner = computedExpressions[field]?.trim();
        return inner ? `(${inner})` : 'NULL';
      }
      return `data->>'${field}'`;
    });
    groupByClause = ` GROUP BY ${groupByParts.join(', ')}`;
  }

  // Construir la cláusula LIMIT y OFFSET
  let limitOffsetClause = '';
  if (config.limit && config.limit > 0) {
    limitOffsetClause += ` LIMIT ${config.limit}`;
  }
  if (config.offset && config.offset > 0) {
    limitOffsetClause += ` OFFSET ${config.offset}`;
  }

  // Construir la consulta SQL final
  const sql = `
    SELECT ${selectClause}
    FROM ckan_data_generic
    WHERE ${whereClause}
    ${groupByClause}
    ${limitOffsetClause}
  `.trim().replace(/\s+/g, ' ');

  console.log('Consulta CKAN generada:', sql);
  return sql;
}



/*function buildCKANQueryFromConfig(config: any, resourceName: string): string {
  // Extraer campos seleccionados
  const selectedFields = config.selectedFields || [];
  const groupByFields = (config.groupByFields || []);

  // Construir la cláusula SELECT
  let selectClause = '';
  if (selectedFields.length > 0) {
    const selectParts = selectedFields.map((field: string) => {
      // Verificar si el campo está en GROUP BY
      const isInGroupBy = groupByFields.includes(field);
      
      if (isInGroupBy) {
        // Campo en GROUP BY: extraer como texto
        return `data->>'${field}' AS "${field}"`;
      } else {
        // Campo no en GROUP BY: verificar si parece ser numérico
        const lowerField = field.toLowerCase();
        const isLikelyNumeric = lowerField.includes('población') || 
                               lowerField.includes('total') || 
                               lowerField.includes('número') ||
                               lowerField.includes('nº') ||
                               lowerField.includes('cantidad') ||
                               lowerField.includes('valor') ||
                               lowerField.includes('superficie') ||
                               lowerField.includes('área') ||
                               lowerField.includes('ha') ||
                               lowerField.includes('hectáreas') ||
                               /\d/.test(field) || // Si contiene dígitos
                               field.includes('('); // Si tiene paréntesis (unidades)
        
        if (isLikelyNumeric) {
          return `SUM((data->>'${field}')::numeric) AS "${field}"`;
        } else {
          // Si no parece numérico, usar MAX o MIN para agregar
          return `data->>'${field}' AS "${field}"`;
          //return `MAX(data->>'${field}') AS "${field}"`;
        }
      }
    });
    
    selectClause = selectParts.join(', ');
  } else {
    selectClause = 'data';
  }

  // Construir la cláusula WHERE (filtros)
  let whereClause = `resource_name = '${resourceName}'`;
  
  if (config.filters && config.filters.length > 0) {
    config.filters.forEach((filter: any) => {
      // EL CAMBIO ESTÁ AQUÍ: Usar directamente filter.field sin dividir por '].'
      const fieldName = filter.field;
      const operator = filter.operator;
      let value = filter.value;
      
      // Escapar comillas simples en el valor
      value = value.replace(/'/g, "''");
      
      // Determinar si el campo es numérico para decidir si convertir
      const lowerField = fieldName.toLowerCase();
      const isNumericField = lowerField.includes('fecha') || 
                            lowerField.includes('año') ||
                            lowerField.includes('población') ||
                            lowerField.includes('total') ||
                            /\d/.test(fieldName);
      
      if (operator === 'ILIKE') {
        whereClause += ` AND data->>'${fieldName}' ILIKE '%${value}%'`;
      } else if (isNumericField) {
        // Para campos numéricos, convertir a numeric y comparar como número
        whereClause += ` AND (data->>'${fieldName}')::numeric ${operator} ${value}`;
      } else {
        // Para campos de texto, comparar como texto
        whereClause += ` AND data->>'${fieldName}' ${operator} '${value}'`;
      }
    });
  }

  // Construir la cláusula GROUP BY
  let groupByClause = '';
  if (groupByFields.length > 0) {
    const groupByParts = groupByFields.map((field: string) => {
      return `data->>'${field}'`;
    });
    groupByClause = ` GROUP BY ${groupByParts.join(', ')}`;
  }

  // Construir la cláusula LIMIT y OFFSET
  let limitOffsetClause = '';
  if (config.limit && config.limit > 0) {
    limitOffsetClause += ` LIMIT ${config.limit}`;
  }
  if (config.offset && config.offset > 0) {
    limitOffsetClause += ` OFFSET ${config.offset}`;
  }

  // Construir la consulta SQL final
  const sql = `
    SELECT ${selectClause}
    FROM ckan_data_generic
    WHERE ${whereClause}
    ${groupByClause}
    ${limitOffsetClause}
  `.trim().replace(/\s+/g, ' ');

  console.log('Consulta CKAN generada:', sql);
  return sql;
}*/

/**
 * Función auxiliar para extraer nombre de recurso de baseTable
 */
/*function extractResourceNameFromBaseTable(baseTable: string): string {
  // Ejemplo: "ckan_data_generic[datos_de_poblacion_total_por_provincias_y_sexo]"
  const match = baseTable.match(/ckan_data_generic\[(.*?)\]/);
  if (!match) {
    throw new Error(`Formato de baseTable inválido: ${baseTable}`);
  }
  return match[1];
}*/

/**
 * Aplica una agregación a un array de datos (para CKAN)
 */
function applyAggregation(
  data: any[],
  field: string,
  aggregation: string
): number {
  switch (aggregation) {
    case 'SUM':
      return data.reduce((sum: number, row: any) => sum + (row[field] || 0), 0)

    case 'AVG':
      const sum = data.reduce((s: number, row: any) => s + (row[field] || 0), 0)
      return data.length > 0 ? sum / data.length : 0

    case 'COUNT':
      return data.length

    case 'MAX':
      const values = data.map((row: any) => row[field] || 0)
      return values.length > 0 ? Math.max(...values) : 0

    case 'MIN':
      const vals = data.map((row: any) => row[field] || 0)
      return vals.length > 0 ? Math.min(...vals) : 0

    default:
      return 0
  }
}

/**
 * Valida que la configuración sea válida
 */
export function validateConfig(config: ComponentConfig): {
  valid: boolean
  errors: string[]
} {
  const errors: string[] = []

  if (!config.dataSource) {
    errors.push('Debe seleccionar una fuente de datos')
  }

  if (!config.componentType) {
    errors.push('Debe seleccionar un tipo de componente')
  }

  if (!config.metadata?.title) {
    errors.push('Debe ingresar un título')
  }

  return {
    valid: errors.length === 0,
    errors
  }
}

/**
 * Obtiene datos de ejemplo para la preview
 */
export async function getPreviewData(config: ComponentConfig): Promise<any> {
  const { dataSource } = config

  console.log("config", config);

  try {
    const { data, error } = await supabase
      .from(dataSource)
      .select('*')
      .limit(10)

    if (error) throw error
    return data || []
  } catch (error) {
    console.error('Error getting preview data:', error)
    return []
  }
}